import { T, formatCents, komboCountLabel, tt, useModalFocus, type KomboCount } from '@agos/shared'
import { ChevronDown, Pizza, X } from 'lucide-react'
import { useRef, useState } from 'react'

import {
  emptyKombo,
  isComplete,
  isOptionalSlot,
  komboName,
  komboParts,
  komboProblem,
  komboTotalCents,
  selectablePizzas,
  type KomboPizza,
  type KomboSlots,
} from '@agos/shared'

/**
 * Several pizzas in one box, priced as the dearest of them at the kombo size. How many is the
 * franchise's setting - `count`, §9.798: `max` selects, and the box can be added once `min` are chosen.
 *
 * The arithmetic is in `kombo.ts` and tested there — including the part that is easy to get wrong and
 * expensive when it is: a kombo is charged the **highest** of its three, at the kombo's own size, not
 * the sum and not the base price. Legacy computes it the same way and its comment explains why the size
 * lookup falls back twice.
 *
 * The line it produces carries a **negative product id**, the convention the till, the kitchen board
 * and `OrderCreationController` already share for something assembled rather than picked. Its name is
 * the only description the kitchen gets, which is why the three pizzas are spelled out in it.
 */
export function KomboBuilder({
  pizzas,
  komboCm,
  count,
  onAdd,
  onClose,
}: {
  pizzas: readonly KomboPizza[]
  komboCm: number
  /** §9.798 - how many pizzas this franchise's kombo holds, from `komboPizzaCount`. */
  count: KomboCount
  onAdd: (line: {
    name: string
    unitPriceCents: number
    parts?: { productId?: number; name: string; unitPriceCents: number; quantity: number }[]
    /** §10A.26 — always `'kombo'` from here, which is what makes the `max` bound apply. */
    composition?: 'kombo'
  }) => void
  onClose: () => void
}) {
  const [slots, setSlots] = useState<KomboSlots>(() => emptyKombo(count))
  const options = selectablePizzas(pizzas)
  const total = komboTotalCents(slots, pizzas, komboCm, count.min)
  const problem = komboProblem(slots, pizzas, count.min)
  const ready = isComplete(slots, count.min) && problem === ''

  /* §9.489, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing, on a customer's own keyboard.
     **The role is on the card, not the wrapper**: the wrapper is the dismiss scrim and says `role="presentation"`
     so it is not itself announced, which is §9.469's rule arrived at from this markup. The close is
     unconditional because the scrim already calls it on a tap with no guard, and this overlay holds no write. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-kombo-modal
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={tt('Kombo pizza')}
      ref={panelRef}
      onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="bg-background rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-md overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-orange-100 flex items-center justify-center">
              <Pizza className="w-5 h-5 text-orange-500" />
            </div>
            <div className="text-left">
              <h2 className="font-bold text-lg leading-tight">
                <T>Kombo pizza</T>
              </h2>
              <p className="text-xs text-muted-foreground">
                <span data-kombo-count>{komboCountLabel(count)} pizza</span> · {komboCm} cm
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-kombo-close
            aria-label={tt('Zavrieť')}
            data-dialog-cancel
            className="p-2.5 rounded-full hover:bg-secondary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          {slots.map((slot, index) => (
            <label key={index} className="block space-y-1">
              <span className="text-xs font-semibold text-muted-foreground">
                {index + 1}. <T>diel</T>
                {isOptionalSlot(index, count) && (
                  <span data-kombo-optional={index}> ({tt('voliteľné')})</span>
                )}
              </span>
              <div className="relative">
                <select
                  data-kombo-slot={index}
                  value={slot === null ? '' : String(slot)}
                  onChange={(event) => {
                    const value = event.target.value
                    const next = [...slots]
                    next[index] = value === '' ? null : Number(value)
                    setSlots(next)
                  }}
                  className="w-full appearance-none rounded-2xl border border-border bg-background px-4 py-3 pr-10 text-sm font-medium"
                >
                  <option value="">{tt('Vyberte pizzu')}</option>
                  {options.map((pizza) => (
                    <option key={pizza.id} value={pizza.id}>
                      {pizza.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              </div>
            </label>
          ))}

          {problem !== '' && (
            <p className="text-xs text-amber-600" data-kombo-problem>
              {problem}
            </p>
          )}
        </div>

        <div className="px-6 pb-6 space-y-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">
              <T>Cena</T>
            </span>
            <span className="text-2xl font-black" data-kombo-total>
              {formatCents(total)}
            </span>
          </div>
          {/* Said out loud, because a customer comparing three prices will otherwise assume the sum. */}
          <p className="text-[11px] text-muted-foreground">
            <T>Kombo sa účtuje ako najdrahšia z vybraných pízz vo veľkosti kombo.</T>
          </p>
          <button
            type="button"
            data-kombo-add
            disabled={!ready}
            onClick={() => {
              // §10A.14 — the three pizzas, each carrying its own catalogue id, which is what
              // lets `ServerPricing` re-price this line rather than trust the figure sent here.
              //
              // §10A.26 — and `composition` says which rule to apply. A kombo is priced `max` by
              // design, so the sum of its parts is far too loose a cap: three pizzas at 9,50 sum to
              // 28,50 and anything under that passed. The server will not infer this, because
              // guessing `max` for a line that is honestly a sum would under-charge it.
              onAdd({
                name: komboName(slots, pizzas),
                unitPriceCents: total,
                parts: komboParts(slots, pizzas, komboCm),
                composition: 'kombo',
              })
              setSlots(emptyKombo(count))
              onClose()
            }}
            className="w-full min-h-[52px] rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold disabled:opacity-50 transition-colors"
          >
            <T>Pridať do košíka</T>
          </button>
        </div>
      </div>
    </div>
  )
}
